/**
 * The browser's side of P1 — what a broken page tells the platform, and, far
 * more importantly, what it does not.
 *
 * WHAT THIS MODULE REFUSES TO SEND, AND WHY THAT IS THE WHOLE POINT. Every
 * capture point here is handed a real `Error`, and a real `Error` is the richest
 * source of study content in the product: a rejected callable's message names
 * forms, institutions and studies. `functions/src/errorTracking/errorRecord.ts`
 * states the platform's position — the raw message lives in Cloud Logging under
 * an 8-hex reference and nowhere else, and no third copy exists. The frontend is
 * where that third copy would be born, because attaching `err.message` is one
 * line and looks like an improvement.
 *
 * So NOTHING derived from the error object is sent except its mapped class. Not
 * the message, not the stack, not the name, and not the reference — even though
 * `reference` is an allow-listed field, parsing one out of a message would be a
 * string derived from the error, which is the rule this module keeps. A caller
 * that legitimately HAS a reference passes it explicitly.
 *
 * THREE FAILURE MODES THIS MODULE MUST NOT HAVE:
 *
 *   - It must never throw. An error reporter that throws turns one broken page
 *     into two, and the second one is unreportable.
 *   - It must never retry. A failed report is dropped. The page is already
 *     broken; a retry loop against a failing endpoint is how that becomes an
 *     outage.
 *   - It must dedupe. A React render loop can fire hundreds of identical errors
 *     in a second. The server's per-caller limiter bounds the damage, but the
 *     browser should not be the thing that needs bounding — and the limiter
 *     would spend the page's whole budget on one repeated fault.
 *
 * The contract it sends against is `functions/src/errorTracking/clientErrorIngest.ts`.
 * The two live in different codebases with no shared package, so
 * `errorReporter.test.ts` reads the server file and fails if they drift.
 */

/** The ways a page fails that the frontend can actually tell apart. */
export const CLIENT_ERROR_CLASSES = [
  'RENDER_ERROR',
  'UNHANDLED_REJECTION',
  'WINDOW_ERROR',
  'CALLABLE_FAILURE',
  'CALLABLE_NOT_REACHED',
] as const;

/**
 * The only callable names the server accepts in `callableName`; a mirror of
 * `REPORTABLE_CALLABLES` in `clientErrorIngest.ts`. A name outside this list is
 * OMITTED from the report, never sent, because the server would refuse the whole
 * report. Equal to the set of calls the page opts in to retry; a test in
 * `errorReporter.test.ts` keeps all three in step.
 */
export const REPORTABLE_CALLABLES = [
  'saveCrfFormV2',
  'generateCRFVariablesV2',
  'listCrfFormsV2',
  'fillVisitMatrixV2',
] as const;

export type ClientErrorClass = (typeof CLIENT_ERROR_CLASSES)[number];

/** Mirrors the endpoint's ACCEPTED_FIELDS. Nothing else may be sent. */
export const REPORT_FIELDS = [
  'errorClass',
  'route',
  'httpStatus',
  'projectId',
  'formCode',
  'reference',
  'callableName',
] as const;

/**
 * Identical errors inside this window collapse to one report. Ten seconds is
 * long enough to swallow a render loop and short enough that a fault which is
 * genuinely still happening a minute later is still counted.
 */
export const DEDUPE_WINDOW_MS = 10_000;

/** Bound on the dedupe table, so a page cycling routes cannot grow it forever. */
export const DEDUPE_MAX_KEYS = 200;

export interface ClientErrorReport {
  errorClass: ClientErrorClass;
  route?: string;
  httpStatus?: number;
  projectId?: string;
  formCode?: string;
  reference?: string;
  callableName?: string;
}

/**
 * The server's own shapes, copied deliberately rather than imported — the
 * frontend cannot import from `functions/`. A value that would fail these is
 * OMITTED rather than sent: a report rejected with invalid-argument tells us
 * nothing, and a page that produces one produces it every single time.
 */
const ROUTE_SHAPE = /^[A-Za-z0-9/_:.-]{1,128}$/;
const PROJECT_ID_SHAPE = /^[A-Za-z0-9_-]{1,128}$/;
const FORM_CODE_SHAPE = /^[A-Z0-9_-]{1,16}$/;
const REFERENCE_SHAPE = /^[0-9a-f]{8}$/;

/**
 * Collapse a concrete URL path to its ROUTE PATTERN.
 *
 * `/project/abc123/crf-builder` becomes `/project/:projectId/crf-builder`. The
 * store fingerprints on the route (`errorStore.ts`), so sending the concrete
 * path would split one broken page into one fingerprint per project and hide
 * the fact that it is broken for everybody.
 *
 * Returns undefined when the result would not satisfy the server's shape, so a
 * page with an exotic URL reports without a route rather than not at all.
 */
export function normalizeRoute(pathname: string): string | undefined {
  if (typeof pathname !== 'string' || pathname === '') return undefined;

  const withoutQuery = pathname.split(/[?#]/)[0];
  const normalized = withoutQuery
    .replace(/^\/project\/[^/]+/, '/project/:projectId')
    .replace(/^\/invite\/[^/]+/, '/invite/:token');

  return ROUTE_SHAPE.test(normalized) ? normalized : undefined;
}

/** The project id from the path, when the route has one. Never invented. */
export function projectIdFromPath(pathname: string): string | undefined {
  if (typeof pathname !== 'string') return undefined;
  const match = /^\/project\/([^/?#]+)/.exec(pathname.split(/[?#]/)[0]);
  const id = match?.[1];
  return id && PROJECT_ID_SHAPE.test(id) ? id : undefined;
}

/**
 * Which class a rejection belongs to, decided on the error's SHAPE rather than
 * its text. A `firebase/functions` callable rejects with a FirebaseError whose
 * `code` reads `functions/<status>`; that prefix is a structural fact about the
 * SDK, not a message we are reading.
 */
export function classifyRejection(reason: unknown): ClientErrorClass {
  const code = (reason as { code?: unknown } | null | undefined)?.code;
  if (typeof code === 'string' && code.startsWith('functions/')) return 'CALLABLE_FAILURE';
  return 'UNHANDLED_REJECTION';
}

/** Fields a caller may attach explicitly. None is ever read off an error. */
export interface ReportExtras {
  httpStatus?: number;
  formCode?: string;
  reference?: string;
  /** Sent only when it is one of REPORTABLE_CALLABLES; otherwise omitted. */
  callableName?: string;
}

export interface ReporterDeps {
  send: (report: ClientErrorReport) => Promise<unknown> | unknown;
  now?: () => number;
  pathname?: () => string;
  /** Where the reporter's own failures go. Console by default; never rethrown. */
  onInternalError?: (err: unknown) => void;
}

export interface ErrorReporter {
  reportRenderError(extras?: ReportExtras): void;
  reportWindowError(extras?: ReportExtras): void;
  reportRejection(reason: unknown, extras?: ReportExtras): void;
  /**
   * For a callable failure caught at a call site. Nothing calls this yet; a
   * callable rejection that escapes is classified by `classifyRejection` on the
   * unhandledrejection path, and a call refused before reaching the server goes
   * through `reportCallableNotReached` below.
   */
  reportCallableFailure(extras?: ReportExtras): void;
  /**
   * A page call was refused before it reached our server and the page gave up.
   * Called by the listener `installClientErrorReporting` registers with
   * `lib/callable.ts`. Carries the callable's name (from the closed list) and
   * nothing read off the error.
   */
  reportCallableNotReached(extras?: ReportExtras): void;
}

export function createErrorReporter(deps: ReporterDeps): ErrorReporter {
  const now = deps.now ?? (() => Date.now());
  const pathname = deps.pathname ?? (() => '/');
  const onInternalError =
    deps.onInternalError ??
    ((err: unknown) => {
      // Console only. This is the one place a message may be shown, because the
      // console is the developer's machine and not a stored copy.
      console.error('[errorReporter] report failed', err);
    });

  /** key -> last send time. Bounded; see DEDUPE_MAX_KEYS. */
  const recent = new Map<string, number>();

  function shouldSend(key: string, at: number): boolean {
    const last = recent.get(key);
    if (last !== undefined && at - last < DEDUPE_WINDOW_MS) return false;

    if (recent.size >= DEDUPE_MAX_KEYS) {
      for (const [k, t] of recent) {
        if (at - t >= DEDUPE_WINDOW_MS) recent.delete(k);
      }
      while (recent.size >= DEDUPE_MAX_KEYS) {
        const oldest = recent.keys().next();
        if (oldest.done) break;
        recent.delete(oldest.value);
      }
    }

    recent.set(key, at);
    return true;
  }

  /**
   * Build the payload FIELD BY FIELD. Deliberately not a spread or a filter over
   * some larger object: an explicit construction cannot accidentally carry a
   * property that was added upstream, which is exactly how a message field would
   * arrive here one day.
   */
  function buildReport(errorClass: ClientErrorClass, extras?: ReportExtras): ClientErrorReport {
    const path = pathname();
    const report: ClientErrorReport = { errorClass };

    const route = normalizeRoute(path);
    if (route !== undefined) report.route = route;

    const projectId = projectIdFromPath(path);
    if (projectId !== undefined) report.projectId = projectId;

    const status = extras?.httpStatus;
    if (typeof status === 'number' && Number.isInteger(status) && status >= 100 && status <= 599) {
      report.httpStatus = status;
    }
    if (typeof extras?.formCode === 'string' && FORM_CODE_SHAPE.test(extras.formCode)) {
      report.formCode = extras.formCode;
    }
    if (typeof extras?.reference === 'string' && REFERENCE_SHAPE.test(extras.reference)) {
      report.reference = extras.reference;
    }
    if (
      typeof extras?.callableName === 'string' &&
      (REPORTABLE_CALLABLES as readonly string[]).includes(extras.callableName)
    ) {
      report.callableName = extras.callableName;
    }
    return report;
  }

  function emit(errorClass: ClientErrorClass, extras?: ReportExtras): void {
    // Total: every failure below is swallowed. A reporter that can throw is a
    // second fault on a page that already has one.
    try {
      const at = now();
      const report = buildReport(errorClass, extras);

      const key = `${report.errorClass}|${report.route ?? ''}|${report.projectId ?? ''}|${report.callableName ?? ''}`;
      if (!shouldSend(key, at)) return;

      // Fire and forget. No await, no retry, no return value the caller could
      // block on — and the rejection handler is attached synchronously so a
      // failed send never becomes an unhandled rejection.
      Promise.resolve(deps.send(report)).catch(onInternalError);
    } catch (err) {
      onInternalError(err);
    }
  }

  return {
    reportRenderError: (extras) => emit('RENDER_ERROR', extras),
    reportWindowError: (extras) => emit('WINDOW_ERROR', extras),
    reportCallableFailure: (extras) => emit('CALLABLE_FAILURE', extras),
    reportCallableNotReached: (extras) => emit('CALLABLE_NOT_REACHED', extras),
    reportRejection: (reason, extras) => emit(classifyRejection(reason), extras),
  };
}

export interface InstallTarget {
  addEventListener: (type: string, fn: (e: unknown) => void) => void;
  removeEventListener: (type: string, fn: (e: unknown) => void) => void;
}

export interface InstallOptions {
  target: InstallTarget;
  /**
   * An ALREADY-BUILT reporter, not a set of deps — so the global listeners and
   * the ErrorBoundary share one dedupe window. Built separately, a render loop
   * that also triggers an unhandled rejection would be counted twice, once by
   * each table.
   */
  reporter: ErrorReporter;
}

/**
 * Attach the two global capture points and return an uninstaller.
 *
 * NEITHER LISTENER CALLS preventDefault, and neither returns a value. The
 * browser still logs the error to the console, other handlers still run, and a
 * rejection is still reported as unhandled. Capture observes; it does not
 * intervene.
 */
export function installGlobalErrorReporting(opts: InstallOptions): () => void {
  const { reporter } = opts;

  const onError = (): void => {
    // The ErrorEvent carries `message`, `filename` and `error` — all of them
    // free text, none of them read. The event's arrival is the whole signal.
    reporter.reportWindowError();
  };
  const onRejection = (event: unknown): void => {
    reporter.reportRejection((event as { reason?: unknown } | undefined)?.reason);
  };

  opts.target.addEventListener('error', onError);
  opts.target.addEventListener('unhandledrejection', onRejection);

  return () => {
    opts.target.removeEventListener('error', onError);
    opts.target.removeEventListener('unhandledrejection', onRejection);
  };
}
